// Navbar.jsx
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";

function Navbar() {
	const { role, user, logout } = useAuth();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const menuRef = useRef(null);
	const menuButtonRef = useRef(null);
	const navigate = useNavigate();

	const BASE_URL = "https://berrymg.com/gear"; // Adjust as needed

	async function handleLogout() {
		await logout();
		navigate("/login");
		setIsMenuOpen(false);
	}

	const handleMenuToggle = (e) => {
		e.stopPropagation();
		setIsMenuOpen((prev) => !prev);
	};

	const handleUpdatePassword = () => {
		navigate("/update-password");
		setIsMenuOpen(false);
	};

	const handleViewUsers = () => {
		navigate("/view-users");
		setIsMenuOpen(false);
	};

	const TEMPLATE = {
		url: "/gear/card-1050x600.png", // same image as your single-card flow; same-origin recommended
		pxW: 1050, // template pixel width (3.5" * 300dpi)
		pxH: 600, // template pixel height (2.0" * 300dpi)
		// The blank QR spot on your template in PIXELS (from the top-left of the template):
		qr: { x: 615, y: 10, size: 390 }, // tweak to match your design
	};

	async function imgToDataURL(src) {
		const img = await new Promise((resolve, reject) => {
			const i = new Image();
			i.crossOrigin = "anonymous";
			i.onload = () => resolve(i);
			i.onerror = reject;
			i.src = src;
		});
		// draw to canvas to get a same-origin data URL
		const c = document.createElement("canvas");
		c.width = img.width;
		c.height = img.height;
		const ctx = c.getContext("2d");
		ctx.drawImage(img, 0, 0);
		return c.toDataURL("image/png");
	}

	// 👉 NEW: generate a PDF sheet with all case QRs
	async function handleDownloadQrSheet() {
		try {
			// 1) Fetch all cases
			const { data: cases, error } = await supabase
				.from("cases")
				.select("id, name");
			if (error) throw error;
			if (!cases || cases.length === 0) {
				alert("No cases found.");
				return;
			}

			// 2) Prepare PDF (Letter = 612 x 792 pt)
			const doc = new jsPDF({ unit: "pt", format: "letter" });
			const pageW = doc.internal.pageSize.getWidth();
			const pageH = doc.internal.pageSize.getHeight();

			// Each card is 3.5" x 2" => 252 x 144 pt (1in = 72pt)
			const cardW = 252;
			const cardH = 144;

			// Grid (2 cols x 5 rows = 10 per page)
			const marginX = 36; // 0.5”
			const marginY = 36; // 0.5”
			const colGap = 24; // spacing between columns
			const rowGap = 18; // spacing between rows
			const cols = 2;
			const rows = 4;

			// 3) Load template once as data URL
			const templateDataUrl = await imgToDataURL(TEMPLATE.url);

			// 4) Precompute px->pt scale from template to card slot
			const scaleX = cardW / TEMPLATE.pxW;
			const scaleY = cardH / TEMPLATE.pxH; // should match scaleX if aspect matches
			const qrSizePt = TEMPLATE.qr.size * scaleX;
			const qrXPt = TEMPLATE.qr.x * scaleX;
			const qrYPt = TEMPLATE.qr.y * scaleY;

			let col = 0,
				row = 0;

			for (let i = 0; i < cases.length; i++) {
				const c = cases[i];
				const caseUrl = `${BASE_URL}/cases/${c.id}`;

				// Build QR for this case
				const qrDataUrl = await QRCode.toDataURL(caseUrl, {
					errorCorrectionLevel: "H",
					margin: 1,
					scale: 8,
					color: { dark: "#000000", light: "#FFFFFF" },
				});

				// Position of this card (top-left corner)
				const cardX = marginX + col * (cardW + colGap);
				const cardY = marginY + row * (cardH + rowGap);

				// Draw template card
				doc.addImage(
					templateDataUrl,
					"PNG",
					cardX,
					cardY,
					cardW,
					cardH,
					undefined,
					"FAST"
				);

				// Draw QR at the exact mapped spot
				doc.addImage(
					qrDataUrl,
					"PNG",
					cardX + qrXPt,
					cardY + qrYPt,
					qrSizePt,
					qrSizePt,
					undefined,
					"FAST"
				);

				// Next card position
				col++;
				if (col >= cols) {
					col = 0;
					row++;
					if (row >= rows && i < cases.length - 1) {
						doc.addPage();
						row = 0;
					}
				}
			}

			doc.save("case-qr-sheet.pdf");
			setIsMenuOpen(false);
		} catch (err) {
			console.error("Failed to generate QR sheet:", err);
			alert("Failed to generate QR sheet. Check console for details.");
		}
	}

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				menuRef.current &&
				!menuRef.current.contains(event.target) &&
				!menuButtonRef.current.contains(event.target)
			) {
				setIsMenuOpen(false);
			}
		};
		document.addEventListener("click", handleClickOutside);
		return () => document.removeEventListener("click", handleClickOutside);
	}, []);

	return (
		<nav className="navbar">
			<Link to="/">Cases</Link>
			<Link to="/kits">Kits</Link>
			{role === "admin" && <Link to="/problem-gear">Problem Gear</Link>}

			{user ? (
				<>
					<button
						ref={menuButtonRef}
						onClick={handleMenuToggle}
						className="button account-button"
					>
						<i className="pi pi-user"></i>
						Account
					</button>

					{isMenuOpen && (
						<div ref={menuRef} className="account-menu">
							<button
								onClick={handleUpdatePassword}
								className="button"
							>
								<i className="pi pi-pencil"></i>
								Update Password
							</button>

							{role === "admin" && (
								<>
									<button
										onClick={handleViewUsers}
										className="button"
									>
										<i className="pi pi-users"></i>
										Users
									</button>
									<button
										onClick={handleDownloadQrSheet}
										className="button"
									>
										<i className="pi pi-download"></i>
										Download QR Cards (PDF)
									</button>
								</>
							)}

							<button onClick={handleLogout} className="button">
								<i className="pi pi-sign-out"></i>
								Logout
							</button>
						</div>
					)}
				</>
			) : (
				<button
					onClick={() => navigate("/login")}
					className="button account-button"
				>
					<i className="pi pi-sign-in"></i>
					Login
				</button>
			)}
		</nav>
	);
}

export default Navbar;
